// РОУТЕР ОБОЛОЧКИ — адресная строка и история браузера.
//
// РЕШЕНИЕ. Native History API + hash-адреса, без библиотеки (`s891`, «Скоуп» тикета «Каркас»).
// Hash не требует rewrite на Vercel, а wouter отклонён именно потому, что завёл бы вторую истину
// о позиции: позиция живёт полем `step` конверта, адрес её ОТРАЖАЕТ.
//
// ПОЧЕМУ РОУТЕР СЧИТАЕТ СВОЮ ГЛУБИНУ. `history.length` считает и чужие записи — страницу, с
// которой пришли, прошлые визиты, в jsdom ещё и записи соседнего теста. Единственный честный
// ответ на вопрос «есть ли куда вернуться ВНУТРИ визарда» — счётчик собственных `pushState`.
// Цена ошибки здесь не косметическая: `history.back()` при нуле своих записей уносит человека
// с сайта на страницу входа — ровно то, что хром §4 запрещает.
//
// ⚠️ ГРАНИЦА. Роутер не знает ни про конверт, ни про React: он переводит адрес ⟷ история.
// Кто имеет право на шаг и что на нём показывать — дело оболочки.

import { parseAddress, formatAddress } from './address.js';

/**
 * СИГНАЛ ЛЕНДИНГА (`s956.d3`): кнопка Begin ведёт на `/begin?from=landing`. Форма сигнала — выбор
 * CC (тикет `/begin`, скоуп п.2): параметр, а не хеш. Хеш уже занят позицией, и `#s2` на пустом
 * конверте по правилу шапки провайдера возвращает к конверту, то есть на шаг 1; менять это
 * правило ради одного входа значило бы сломать прямые ссылки на шаг.
 */
export const LANDING_PARAM = 'from';
export const LANDING_VALUE = 'landing';

/**
 * Поднять роутер над `window`.
 *
 * @param {{win?: Window}} [opts] окно подменяется в тестах; по умолчанию — глобальное
 */
export function createRouter({ win = window } = {}) {
  const subscribers = new Set();
  let depth = 0; // сколько записей положил ЭТОТ роутер — см. шапку, `history.length` врёт

  const notify = () => {
    const address = parseAddress(win.location.hash);
    for (const fn of subscribers) fn(address);
  };

  const onPopstate = () => {
    // Уход назад за пределы своих записей — единственное, что уменьшает глубину.
    if (depth > 0) depth -= 1;
    notify();
  };

  // ⚠️ СЛУШАТЕЛЬ СНИМАЕТСЯ И СТАВИТСЯ ОБРАТНО, А НЕ ОДИН РАЗ НАВСЕГДА.
  // Поймано e2e в настоящем браузере (cc-124): `React.StrictMode` в деве гоняет эффект дважды —
  // маунт, размонтирование, маунт. Cleanup звал `stop()`, слушатель окна снимался, а второй
  // проход эффекта его уже не возвращал: браузерный «назад» переставал работать МОЛЧА, и только
  // в деве. В jsdom этого не видно — там оболочка монтируется без StrictMode.
  let armed = false;
  const arm = () => {
    if (armed) return;
    win.addEventListener('popstate', onPopstate);
    armed = true;
  };
  arm();

  const write = (address, replace) => {
    const raw = typeof address === 'string' ? address : formatAddress(address);
    const parsed = parseAddress(raw);
    if (!parsed) throw new Error(`роутеру дали негодный адрес: ${raw}`);

    // Нормализация обязательна: конверт хранит адрес без решётки (`s3/2/b`), а `pushState('s3/2/b')`
    // сменил бы ПУТЬ, а не хэш — визард уехал бы на несуществующий маршрут.
    const hash = formatAddress(parsed);

    if (replace) {
      win.history.replaceState({ address: hash }, '', hash);
    } else {
      win.history.pushState({ address: hash }, '', hash);
      depth += 1;
    }
    return parseAddress(hash);
  };

  /**
   * ⭐ СИГНАЛ ОДНОРАЗОВЫЙ. Оставь его в строке — и перезагрузка после `start over` на шаге 1 снова
   * увела бы на шаг 2, а ссылка, скопированная с середины визарда, вела бы себя как кнопка лендинга.
   * Срезается только наш параметр; прочие (`?view=wizard`) и хеш остаются. Подмена, не запись.
   */
  const dropLandingSignal = () => {
    const params = new URLSearchParams(win.location.search);
    if (!params.has(LANDING_PARAM)) return;
    params.delete(LANDING_PARAM);
    const search = params.toString();
    const url = win.location.pathname + (search ? `?${search}` : '') + win.location.hash;
    win.history.replaceState(win.history.state, '', url);
  };

  return {
    /** Разобранный текущий адрес, или `null` если в строке мусор (или её нет вовсе). */
    current() {
      return parseAddress(win.location.hash);
    },

    /**
     * Сырой хэш адресной строки — `''`, если его нет вовсе.
     *
     * ⚠️ ЭТО НЕ ДУБЛЬ `current()`. Он отвечает `null` и на мусор (`#s99/beta`), и на пустую
     * строку, а различать их обязательно: мусор это опечатка человека, которую адрес
     * исправляет, а пустая строка — ГОЛЫЙ КОРЕНЬ, то есть отдельный вход (`s895.d1`).
     */
    hash() {
      return win.location.hash || '';
    },

    /** Сколько записей визарда лежит позади. Ноль = вернуться внутри визарда некуда. */
    get depth() {
      return depth;
    },

    /**
     * Первая запись сессии — `replaceState`, а не `pushState`.
     * Иначе на входе в визард в истории оказываются две записи подряд с одним адресом, и первое
     * нажатие «назад» выглядит как «ничего не произошло».
     *
     * ⭐ `keepAddress` — ГОЛЫЙ КОРЕНЬ ПОД КОНВЕРТ НЕ ПЕРЕПИСЫВАЕТСЯ (`s895.d1`, слово Кирилла).
     * До cc-130 здесь стоял безусловный `write(address, true)`: конверт на `s4/2/b` + заход на
     * `?view=wizard` без хеша давал `#s4/2/b` и `step 04 / 07`, то есть лендинг в состоянии 2
     * был недостижим вовсе (замер cc-129, тело тикета B3-4). Теперь вход различается адресом:
     * адрес с хешем сильнее (перезагрузка возвращает на тот же шаг, хром §4), голый корень
     * остаётся голым — позиция появится в строке, когда человек нажмёт `continue`.
     *
     * ⚠️ САМ РОУТЕР ПРО КОНВЕРТ НЕ ЗНАЕТ и знать не должен (граница в шапке файла): «есть ли
     * работа» решает оболочка и говорит это одним флагом.
     *
     * @param {{keepAddress?: boolean}} [opts] `true` — не трогать адресную строку вовсе
     */
    start(address, { keepAddress = false } = {}) {
      arm();
      dropLandingSignal();
      if (keepAddress) return parseAddress(win.location.hash);
      return write(address, true);
    },

    /**
     * Пришёл ли человек с лендинга (`?from=landing`, `s956.d3`). Читается ДО `start()`: тот
     * параметр срезает.
     */
    fromLanding() {
      return new URLSearchParams(win.location.search).get(LANDING_PARAM) === LANDING_VALUE;
    },

    /** Переход на шаг или подшаг: новая запись в истории (хром §4 `:55`). */
    go(address) {
      return write(address, false);
    },

    /** Переход без новой записи — подмена текущей позиции. */
    replace(address) {
      return write(address, true);
    },

    /**
     * «Назад» внутри визарда. Возвращает `true`, если отдал команду истории.
     *
     * При нуле своих записей НЕ зовёт `history.back()`: там чужая страница, а не предыдущий шаг.
     * Оболочка в этом случае сама решает, куда отступить (и делает это `replace`, чтобы не
     * копить записи, которых человек не проходил).
     */
    back() {
      if (depth === 0) return false;
      win.history.back();
      return true;
    },

    /** Подписка на смену адреса (`popstate`). Возвращает отписку. */
    subscribe(fn) {
      arm();
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },

    /** Слушает ли роутер окно прямо сейчас — чтобы «снял и не вернул» было видно тесту. */
    get armed() {
      return armed;
    },

    /** Снять слушателя окна. Роутер можно поднять обратно — `start()` или `subscribe()`. */
    stop() {
      if (armed) win.removeEventListener('popstate', onPopstate);
      armed = false;
      subscribers.clear();
    },
  };
}
